import { apiClient, unwrap } from '@/lib/apiClient';
import { compactParams } from '@/lib/queryParams';
import type { ApiSuccess, PaginationMeta } from '@/types/api';
import type {
  TicketDetail,
  TicketFilterOptions,
  TicketPerson,
  TicketPriority,
  TicketQueueCounts,
  TicketStatus,
  TicketSummary,
  TicketTrendMonth,
} from '@/types/support';

/** Which tickets are being looked at — shared by the list, its counts and the chart. */
export interface TicketScopeParams {
  institute?: string;
  state?: string;
  raisedBy?: string;
  /** `YYYY-MM-DD`, inclusive, Indian calendar days. */
  from?: string;
  to?: string;
  search?: string;
}

/**
 * Not a status of its own: every status that still needs somebody — the set
 * the queue's Open and Urgent counts are taken over. Mirrors the backend's
 * `LIVE_TICKETS_FILTER`.
 */
export const LIVE_TICKETS = 'LIVE';

/** A real status, or every live one at once. */
export type TicketStatusFilter = TicketStatus | typeof LIVE_TICKETS;

export interface TicketQueueParams extends TicketScopeParams {
  page: number;
  limit: number;
  status?: TicketStatusFilter;
  priority?: TicketPriority;
  assigned?: 'me' | 'unassigned';
}


/** Every support call the platform console makes (CLAUDE.md §6). */
export const platformSupportApi = {
  list: async (params: TicketQueueParams) => {
    const response = await apiClient.get<ApiSuccess<{ items: TicketSummary[]; counts: TicketQueueCounts }>>(
      '/platform/support/tickets',
      { params: compactParams({ ...params }) },
    );
    const { items, counts } = response.data.data;
    const meta: PaginationMeta = response.data.meta ?? {
      page: 1,
      limit: params.limit,
      total: items.length,
      totalPages: 1,
    };
    return { items, counts, meta };
  },

  get: (uuid: string) =>
    unwrap(apiClient.get<ApiSuccess<TicketDetail>>(`/platform/support/tickets/${uuid}`)),

  /** An internal note never reaches the school, by email or on screen. */
  reply: (uuid: string, message: string, isInternal: boolean, files: File[]) => {
    const form = new FormData();
    form.append('message', message);
    form.append('isInternal', String(isInternal));
    for (const file of files) form.append('attachments', file);
    return unwrap(
      // `emailed`: whether the mail to the school was actually handed over.
      apiClient.post<ApiSuccess<TicketDetail & { emailed: boolean }>>(
        `/platform/support/tickets/${uuid}/replies`,
        form,
        { headers: { 'Content-Type': undefined } },
      ),
    );
  },

  update: (
    uuid: string,
    changes: { status?: TicketStatus; priority?: TicketPriority; assignedToUuid?: string | null },
  ) => unwrap(apiClient.patch<ApiSuccess<TicketDetail>>(`/platform/support/tickets/${uuid}`, changes)),

  /** Tickets raised per month, for the chart above the queue. */
  trend: (params: TicketScopeParams & { months: number }) =>
    unwrap(
      apiClient.get<ApiSuccess<TicketTrendMonth[]>>('/platform/support/trend', { params: compactParams({ ...params }) }),
    ),

  filterOptions: () =>
    unwrap(apiClient.get<ApiSuccess<TicketFilterOptions>>('/platform/support/filters')),

  staff: () => unwrap(apiClient.get<ApiSuccess<TicketPerson[]>>('/platform/support/staff')),

  attachmentPath: (ticketUuid: string, attachmentUuid: string) =>
    `/platform/support/tickets/${ticketUuid}/attachments/${attachmentUuid}`,
};
